import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  ViewProjectExpenseDetails,
  ViewProjectFeeOrExpenseDetails,
} from "../../../../../../../../api/services/project/model";
import { useGetProjectFeesOrExpenseById } from "../../../../../../../../api/services/project/projectApis";
import {
  ITabs,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  MuiTypography,
  MuiViewMorePopOver,
} from "../../../../../../../../components";
import { TaxDetailsFormKeyProps } from "../../../../../../../../components/taxDetails/modelsAndSchemas/Models";
import { TaxDetails } from "../../../../../../../../components/taxDetails/TaxDetails";
import { CURRENCY_FORMAT_MAP } from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import { viewMorePopOverGridSize } from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getFormattedAmountWithCurrency,
  getFullName,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ADD_PROJECT_FEE_AND_EXPENSE_TYPES,
  ProjectFeeTabFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { projectAddFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ViewProjectFeeExpenseModalContainer } from "./ProjectViewFeeExpenseModal.style";

const TAB_INDEX = {
  GENERAL: 0,
  TAX: 1,
};

interface ViewProjectFeeExpenseModalProps {
  open: boolean;
  projectExpenseId: string;
  billingCurrencyName: string;
  onClose: () => void;
}

type ProjectFeeExpenseItem = {
  label: string;
  value?: string | string[] | React.ReactNode;
  showEllipsis?: boolean;
};

type ProjectFieldValues = {
  header: string;
  name?: string;
  items: ProjectFeeExpenseItem[];
  gridSize?: { md: number; sm: number; xs: number; lg?: number };
};
const ProjectSectionBox: React.FC<{
  header?: string;
  expenseItems: ProjectFeeExpenseItem[];
  gridSize: { md: number; sm: number; xs: number; lg?: number };
}> = ({ header, expenseItems, gridSize = { lg: 4, md: 3, xs: 12, sm: 3 } }) => {
  const { isMobile } = useMediaQueries();
  return (
    <MuiBox className="container-box">
      {header && (
        <MuiTypography variant="h2Bold" className="container-header">
          {header}
        </MuiTypography>
      )}
      <MuiGrid
        container
        spacing={2}
        rowSpacing={isMobile ? 2 : 5}
        className="configuration-container"
      >
        {expenseItems.map((item) => (
          <MuiGrid key={item.label} container size={gridSize}>
            <MuiDoubleLabel
              label={item.label}
              value={item.value}
              replaceEmptyWithDash={true}
              isValueWithEllipsis={false}
            />
          </MuiGrid>
        ))}
      </MuiGrid>
    </MuiBox>
  );
};

const ViewProjectFeeExpenseModal: React.FC<ViewProjectFeeExpenseModalProps> = ({
  open,
  projectExpenseId,
  billingCurrencyName,
  onClose,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };
  const [projectExpenseSummary, setProjectExpenseSummary] = useState<
    ProjectFieldValues[]
  >([]);
  const [projectDetailsSections, setProjectDetailsSections] = useState<
    ProjectFieldValues[] | null
  >();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const {
    mutate: getProjectExpenseById,
    data: projectExpense,
    isPending,
  } = useGetProjectFeesOrExpenseById();
  const { fetchLabel, commonLabels } = useSiteCore();
  const methods = useForm({
    mode: "all",
  });
  const { setValue } = methods;
  const dispatch = useDispatch();
  const billingCurrencyFormat = CURRENCY_FORMAT_MAP[billingCurrencyName];
  // --- CONSTANTS ---
  const taxDetailsFormKeys: TaxDetailsFormKeyProps = {
    programFeeKey: ProjectFeeTabFormKey.fee,
    taxKey: ProjectFeeTabFormKey.tax,
    taxPercentageKey: ProjectFeeTabFormKey.taxPercentage,
    taxAmountWithFormatKey: ProjectFeeTabFormKey.taxAmountWithFormat,
    taxAmountKey: ProjectFeeTabFormKey.taxAmount,
    taxEntriesKey: ProjectFeeTabFormKey.taxEntries,
    taxSearchKey: ProjectFeeTabFormKey.taxSearch,
  };

  // --- HELPER FUNCTIONS ---
  const getProjectFeeExpenseLabel = (
    key: keyof typeof projectAddFeesAndExpensesSiteCoreKeys,
  ) => {
    return fetchLabel(
      projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
      projectAddFeesAndExpensesSiteCoreKeys[key],
    );
  };

  const isProjectOrProjectFee = (expense: ViewProjectFeeOrExpenseDetails) => {
    return (
      expense?.type === ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE
    );
  };

  // Reusable billing details section
  const getBillingDetailsSection = (
    projectExpenseDetails: ViewProjectExpenseDetails,
  ) => {
    const isCurrencyMismatch =
      billingCurrencyName !== projectExpenseDetails.feeCurrency?.name;

    const expenseItems = [
      {
        label: getProjectFeeExpenseLabel("billingDetailsCurrency"),
        value: billingCurrencyName,
      },
      ...(isCurrencyMismatch
        ? [
            {
              label: getProjectFeeExpenseLabel("billingDetailsExchangeRate"),
              value: projectExpenseDetails.exchangeRate
                ? `${billingCurrencyName} ${projectExpenseDetails.exchangeRate}`
                : "",
            },
          ]
        : []),
      {
        label: getProjectFeeExpenseLabel("billingDetailsAmount"),
        value: getFormattedAmountWithCurrency(
          projectExpenseDetails.billingAmount,
          billingCurrencyName,
        ),
      },
    ];

    return {
      header: getProjectFeeExpenseLabel("billingDetailsHeading"),
      items: expenseItems,
    };
  };

  // Reusable comment section
  const getCommentSection = (expenseDetails: ViewProjectExpenseDetails) => ({
    header: "",
    items: [
      {
        label: commonLabels.comment,
        value: (
          <MuiDoubleLabel
            label={""}
            replaceEmptyWithDash={true}
            value={
              isNotEmpty(expenseDetails.comments) ? (
                <MuiViewMorePopOver
                  anchorText={commonLabels.viewMore}
                  message={expenseDetails.comments}
                />
              ) : (
                ""
              )
            }
          />
        ),
      },
    ],
    gridSize: viewMorePopOverGridSize,
  });

  // --- EFFECTS ---
  useEffect(() => {
    getProjectExpenseById(projectExpenseId);
  }, []);

  useEffect(() => {
    if (!projectExpense) return;
    setValue(
      taxDetailsFormKeys.programFeeKey,
      projectExpense?.expenseDetails.feeAmount,
    );
    setValue(taxDetailsFormKeys.taxEntriesKey, projectExpense?.taxDetails);
  }, [projectExpense]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [isPending]);

  useEffect(() => {
    if (
      projectExpense &&
      isNotEmpty(projectExpense) &&
      isProjectOrProjectFee(projectExpense)
    ) {
      const { expenseDetails } = projectExpense;
      setProjectExpenseSummary([
        {
          header: getProjectFeeExpenseLabel("summaryTab"),
          items: [
            {
              label: getProjectFeeExpenseLabel("summaryTabAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.billingAmount,
                billingCurrencyName,
              ),
              showEllipsis: false,
            },
            {
              label: getProjectFeeExpenseLabel("summaryTabTotalTaxAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.tax,
                billingCurrencyName,
              ),
              showEllipsis: false,
            },
            {
              label: getProjectFeeExpenseLabel("summaryTabTotalBillingAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.totalAmount,
                billingCurrencyName,
              ),
              showEllipsis: false,
            },
          ],
        },
      ]);
      setProjectDetailsSections([
        {
          header: "",
          items: [
            {
              label: getProjectFeeExpenseLabel("feeName"),
              value: expenseDetails.feeName,
            },
            {
              label: getProjectFeeExpenseLabel("transactionDate"),
              value: convertToUserFormat(
                expenseDetails.transactionDate,
                userCountryDateFormat,
              ),
            },
          ],
        },
        {
          header: getProjectFeeExpenseLabel("feeDetailsHeading"),
          items: [
            {
              label: getProjectFeeExpenseLabel("feeDetailsType"),
              value: expenseDetails.feeType?.name,
            },
            {
              label: getProjectFeeExpenseLabel("feeDetailsCurrency"),
              value: expenseDetails.feeCurrency?.name,
            },
            {
              label: getProjectFeeExpenseLabel("feeDetailsAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.feeAmount,
                expenseDetails.feeCurrency?.name,
              ),
            },
          ],
        },
        getBillingDetailsSection(expenseDetails),
        getCommentSection(expenseDetails),
      ]);
    }
    if (
      projectExpense?.type === ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE
    ) {
      const { expenseDetails } = projectExpense;
      setProjectExpenseSummary([
        {
          header: getProjectFeeExpenseLabel("summaryTab"),
          items: [
            {
              label: getProjectFeeExpenseLabel("summaryTabAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.feeAmount,
                billingCurrencyName,
              ),
            },
            {
              label: getProjectFeeExpenseLabel("summaryTabExchangeRate"),
              value: expenseDetails.exchangeRate
                ? `${billingCurrencyName} ${expenseDetails.exchangeRate}`
                : "",
            },
            {
              label: getProjectFeeExpenseLabel("summaryTabBillingAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.billingAmount,
                billingCurrencyName,
              ),
            },
            {
              label: getProjectFeeExpenseLabel("summaryTabTotalTaxAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.tax,
                billingCurrencyName,
              ),
            },
            {
              label: getProjectFeeExpenseLabel("summaryTabTotalInvoiceAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.totalAmount,
                billingCurrencyName,
              ),
            },
          ],
        },
      ]);
      setProjectDetailsSections([
        {
          header: "",
          items: [
            {
              label: getProjectFeeExpenseLabel("expenseName"),
              value: expenseDetails.feeName,
            },
            {
              label: getProjectFeeExpenseLabel("transactionDate"),
              value: convertToUserFormat(
                expenseDetails.transactionDate,
                userCountryDateFormat,
              ),
            },
            {
              label: getProjectFeeExpenseLabel("incurredBy"),
              value:
                isNotEmpty(expenseDetails.incurredBy) &&
                expenseDetails.incurredBy?.firstName &&
                expenseDetails.incurredBy?.lastName
                  ? getFullName(
                      expenseDetails.incurredBy?.firstName,
                      expenseDetails.incurredBy?.lastName,
                      true,
                    )
                  : "",
            },
          ],
        },
        {
          header: getProjectFeeExpenseLabel("expenseDetailsHeading"),
          items: [
            {
              label: getProjectFeeExpenseLabel("expenseDetailsCategory"),
              value: expenseDetails.expenseCategory?.name,
            },
            {
              label: getProjectFeeExpenseLabel("expenseDetailsType"),
              value: expenseDetails.expenseType?.name,
            },
            {
              label: getProjectFeeExpenseLabel("expenseDetailsUnitPrice"),
              value: expenseDetails?.unitRate?.toString(),
            },
            {
              label: getProjectFeeExpenseLabel("expenseDetailsQuantity"),
              value: expenseDetails?.quantity?.toString(),
            },
            {
              label: getProjectFeeExpenseLabel("expenseDetailsCurrency"),
              value: expenseDetails.feeCurrency.name,
            },
            {
              label: getProjectFeeExpenseLabel("expenseDetailsAmount"),
              value: getFormattedAmountWithCurrency(
                expenseDetails.feeAmount,
                billingCurrencyName,
              ),
            },
          ],
        },
        getBillingDetailsSection(expenseDetails),
        getCommentSection(expenseDetails),
      ]);
    }
  }, [projectExpense]);

  const tabIdGeneral = `projectGeneralTab`;
  const tabIdTax = `projectTaxTab`;
  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: getProjectFeeExpenseLabel("generalTab"),
    },
    {
      id: tabIdTax,
      label: getProjectFeeExpenseLabel("taxTab"),
    },
  ];

  const handleBackButtonClick = () => {
    setTabIndex(TAB_INDEX.GENERAL);
  };

  return (
    <FormProvider {...methods}>
      <MuiModal
        open={open}
        onClose={onClose}
        title={replaceHashWithValue(
          getProjectFeeExpenseLabel("viewFeesAndExpensesHeading"),
          {
            FEEOREXPENSENAME: projectExpense?.expenseDetails.feeName ?? "",
            TYPE: projectExpense?.type
              ? fetchLabel(
                  projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
                  projectExpense?.type,
                )
              : "",
          },
        )}
        showPrimaryButton={false}
        secondaryButtonTitle={commonLabels.close}
        onSecondaryButtonClick={onClose}
        actionButtonTitle={
          tabIndex !== TAB_INDEX.GENERAL ? commonLabels.back : undefined
        }
        onActionButtonClick={() => handleBackButtonClick()}
        hasLargeContent
        className="project-view-fee-expense-modal"
      >
        <ViewProjectFeeExpenseModalContainer className="view-fee-expense-modal-container">
          {projectExpenseSummary.map((item) => (
            <ProjectSectionBox
              header={item.header}
              expenseItems={item.items}
              gridSize={gridSize}
              key={`${item.header}-${item.items[0]?.label ?? "section"}`}
            />
          ))}
          <MuiTabs
            tabs={tabs}
            value={tabIndex}
            onChange={(_: React.ChangeEvent<{}>, newValue: number) =>
              setTabIndex(newValue)
            }
            tabsTestIds={tabs.map((tab) => tab.id)}
            className="tabs-container"
          >
            <MuiTabPanel
              panelId="general-panel"
              value={tabIndex}
              index={TAB_INDEX.GENERAL}
              className="tab-panel-details"
              ariaLabelledById={tabIdGeneral}
            >
              <>
                {projectDetailsSections?.map((section) => (
                  <MuiGrid
                    container
                    columnSpacing={2}
                    rowSpacing={5}
                    key={`${section.header}-${section.items[0]?.label ?? "section"}`}
                  >
                    <ProjectSectionBox
                      header={section.header}
                      expenseItems={section.items}
                      gridSize={section.gridSize ?? gridSize}
                    />
                  </MuiGrid>
                ))}
              </>
            </MuiTabPanel>
            <MuiTabPanel
              panelId="fees-tab-history"
              value={tabIndex}
              index={TAB_INDEX.TAX}
              className="tab-panel-history"
              ariaLabelledById={tabIdTax}
            >
              <TaxDetails
                pageId={"addFeesAndExpenseTaxForm"}
                currencyFormat={billingCurrencyFormat}
                currencyName={billingCurrencyName}
                forceUpdate={() => {}}
                currencyId={projectExpense?.expenseDetails.feeCurrency.id ?? ""}
                isInEditMode={false}
                isInViewMode={true}
                taxDetailsFormKeys={taxDetailsFormKeys}
                programOrProjectFee={
                  projectExpense?.expenseDetails.billingAmount.toString() || ""
                }
              />
            </MuiTabPanel>
          </MuiTabs>
        </ViewProjectFeeExpenseModalContainer>
      </MuiModal>
    </FormProvider>
  );
};

export default ViewProjectFeeExpenseModal;
